/**
 * @file StatementPrintSummary.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-02
 * @modified 2026-10-02
 *
 * What a statement print run actually got onto paper, shown when the print
 * job finishes (Generate Statements → Print).
 *
 * The server sends a run in parts and only counts a part once the printer has
 * confirmed it (routes/statements.js POST /print). When a part is not
 * confirmed — paper out, cover open, the print server dropping the job — it
 * stops, and this lists every customer not confirmed: the part that failed and
 * everything after it. "Reprint these" sends exactly that list back to the
 * printer (statement pages where those were missing, invoices where those
 * were), without a new run or any approve/unapprove.
 *
 * The same list also comes from the run's delivery log
 * (GET /api/statements/print-unconfirmed, `result.fromLog`) — after the screen
 * was left mid-print, the print's result has expired, or the server restarted
 * part-way. It is then everyone in the run sent to the printer and not
 * confirmed, whatever print sent them.
 */
import React from 'react';
import { useSlideOutButton } from './SlideOutButton';
import { accountLabel } from '../utils/statementOrder';
import './StatementPrintSummary.css';

const PART_WORDS = {
  confirmed: 'Printed',
  unconfirmed: 'Not confirmed',
  not_sent: 'Not sent',
  not_needed: '—',
  pending: 'Not confirmed'
};

const plural = (n, word) => `${n} ${word}${n !== 1 ? 's' : ''}`;

/** "Printed 226 statements (Tray1) and 658 invoices (Tray2)." from the result. */
export function printHeadline(result) {
  const r = result || {};
  if (r.fromLog) {
    const n = (r.unconfirmed || []).length;
    return `${plural(n, 'customer')} in this run ${n === 1 ? 'was' : 'were'} sent to the printer and not confirmed printed.`;
  }
  const inc = r.include || 'both';
  const stmts = r.statementsPrinted ?? r.printed ?? 0;
  const invs = r.totalInvoices || 0;
  const stmtClause = `${plural(stmts, 'statement')} (${r.statementTray || 'tray 1'})`;
  const invClause = `${plural(invs, 'invoice')} (${r.invoiceTray || 'tray 2'})`;
  if (!stmts && !invs) return r.success ? 'Nothing needed printing.' : 'Nothing was confirmed printed.';
  if (inc === 'invoices' || (!stmts && invs)) return `Printed ${invClause}.`;
  if (inc === 'statement' || (stmts && !invs)) return `Printed ${stmtClause}.`;
  return `Printed ${stmtClause} and ${invClause}.`;
}

/** Plain-text list of what was not confirmed, for the clipboard. */
export function unconfirmedText(result) {
  const r = result || {};
  const lines = [`Statement print — not confirmed (${new Date().toLocaleString('en-GB')})`];
  if (r.stopped?.text) lines.push(r.stopped.text);
  if (r.failure) lines.push(r.failure);
  lines.push('');
  for (const u of r.unconfirmed || []) {
    lines.push(`${accountLabel(u.account_no, u.company_name)}\tstatement: ${PART_WORDS[u.statement] || u.statement}\tinvoices: ${PART_WORDS[u.invoices] || u.invoices}`);
  }
  return lines.join('\n');
}

function StatementPrintSummary({ result, onReprint }) {
  const r = result || {};
  const unconfirmed = r.unconfirmed || [];
  const results = r.results || [];
  const ok = r.success && unconfirmed.length === 0;
  const reprintCount = new Set([...(r.reprint?.statements || []), ...(r.reprint?.invoices || [])]).size;
  const canReprint = !!(onReprint && reprintCount > 0);
  const notApprovedNow = unconfirmed.filter(u => u.approved === false).length;
  const interrupted = unconfirmed.some(u => u.interrupted);

  useSlideOutButton([unconfirmed.length, canReprint]);

  const copy = async () => {
    try { await navigator.clipboard.writeText(unconfirmedText(r)); } catch (err) { console.error('Copy failed:', err); }
  };

  return (
    <div className="stmt-print-summary">
      <div className={`sps-headline ${ok ? 'ok' : 'warn'}`}>
        <span className="material-symbols-rounded">{ok ? 'task_alt' : 'print_error'}</span>
        <div>
          <div>{printHeadline(r)}{ok && results.length > 0 ? ' The printer confirmed every part.' : ''}</div>
          {!ok && r.stopped && (
            <div className="sps-stop">
              {r.stopped.text
                ? `${r.stopped.text.charAt(0).toUpperCase()}${r.stopped.text.slice(1)}.`
                : `Printing stopped at part ${r.stopped.part} of ${r.stopped.of} (${r.stopped.kind}${r.stopped.jobId ? `, print job ${r.stopped.jobId}` : ''}): ${r.stopped.reason}.`}
            </div>
          )}
          {!ok && r.failure && <div className="sps-stop">{r.failure}</div>}
        </div>
      </div>

      {unconfirmed.length > 0 && (
        <>
          <p className="sps-note">
            {r.fromLog
              ? <>From the run's delivery log: <strong>{plural(unconfirmed.length, 'customer')}</strong> whose
                  latest print the printer never confirmed{interrupted ? ' (some were still waiting when the print stopped)' : ''}.
                  Check the printer and the paper already out before reprinting: a part already sent may have printed.</>
              : <><strong>{plural(unconfirmed.length, 'customer')}</strong> not confirmed printed — the part that
                  failed and everything after it. Check the printer and the paper already out before reprinting:
                  the failed part may have printed partly.</>}
          </p>
          {notApprovedNow > 0 && (
            <p className="sps-note">{plural(notApprovedNow, 'statement')} here {notApprovedNow === 1 ? 'is' : 'are'} no longer approved and will not be reprinted.</p>
          )}
          <div className="sps-table-wrap">
            <table className="sps-table">
              <thead>
                <tr><th>Account</th><th>Customer</th><th>Statement</th><th>Invoices</th></tr>
              </thead>
              <tbody>
                {unconfirmed.map(u => (
                  <tr key={u.statement_id} title={u.problem || undefined}>
                    <td>{u.account_no || '—'}</td>
                    <td>{u.company_name || ''}</td>
                    <td className={`sps-part sps-${u.statement}`}>{PART_WORDS[u.statement] || u.statement}</td>
                    <td className={`sps-part sps-${u.invoices}`}>
                      {PART_WORDS[u.invoices] || u.invoices}
                      {u.invoices !== 'not_needed' && u.invoiceCount ? ` (${u.invoiceCount})` : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="buttons slideout sps-actions">
            <div>
              <button type="button" className="btn-slideout" onClick={copy}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">content_copy</span></div>
                <span className="btn-label">Copy list</span>
              </button>
            </div>
            {canReprint && (
              <div>
                <button type="button" className="btn-slideout" onClick={() => onReprint(r.reprint, r.options, r.runId)}>
                  <div className="icon-wrapper"><span className="material-symbols-rounded">print</span></div>
                  <span className="btn-label">Reprint these {reprintCount}</span>
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {r.notApproved > 0 && (
        <p className="sps-note">{plural(r.notApproved, 'statement')} asked for were no longer approved and were left out.</p>
      )}
      {(r.recordErrors || []).length > 0 && (
        <p className="sps-note sps-error">
          Printed, but {plural(r.recordErrors.length, 'record')} could not be saved: {r.recordErrors.join('; ')}
        </p>
      )}

      {results.length > 0 && (
        <details className="sps-all" open={ok}>
          <summary>All {plural(results.length, 'customer')}</summary>
          <div className="sps-table-wrap">
            <table className="sps-table">
              <thead>
                <tr><th>Account</th><th>Customer</th><th className="num">Invoices</th><th>Printed</th></tr>
              </thead>
              <tbody>
                {results.map(x => (
                  <tr key={x.statement_id}>
                    <td>{x.account_no || '—'}</td>
                    <td>{x.company_name || ''}</td>
                    <td className="num">{x.invoicesIncluded ? x.invoiceCount : '—'}</td>
                    <td className={x.nothingToPrint ? 'sps-part' : `sps-part ${x.confirmed ? 'sps-confirmed' : 'sps-unconfirmed'}`}>
                      {x.nothingToPrint ? 'Nothing to print' : x.confirmed ? 'Confirmed' : 'Not confirmed'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </div>
  );
}

export default StatementPrintSummary;
